import {QueryClient} from '@tanstack/react-query';
import axios, {AxiosRequestConfig} from 'axios';
import {getActiveWorkspaceId} from '../workspace/active-workspace-id';
import {isAbsoluteUrl} from '@ui/utils/urls/is-absolute-url';
import {errorStatusIs} from '@common/http/error-status-is';
import {getEchoSocketId} from '@common/http/get-echo-socket-id';
import {Capacitor} from '@capacitor/core'
import { getTokenSync } from '@common/auth/bearer-token';

const isNative = Capacitor.isNativePlatform();

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30000,
      retry: (failureCount, err) => {
        return (
          !errorStatusIs(err, 401) &&
          !errorStatusIs(err, 403) &&
          !errorStatusIs(err, 404) &&
          failureCount < 2
        );
      },
    },
  },
});

const internalEndpoints = ['auth', 'secure', 'log-viewer', 'horizon'];
const mobileApiEndpoints = isNative ? ['auth/login', 'auth/logout', 'auth/register']:  [];

export const apiClient = axios.create();
apiClient.defaults.responseType = 'json';
apiClient.defaults.baseURL = import.meta.env.VITE_API_URL;
apiClient.defaults.headers.common['Accept'] = 'application/json';
apiClient.defaults.timeout = 0;

apiClient.interceptors.response.use(
  r => r,
  err => {
    if (axios.isCancel?.(err) || err.code === 'ERR_CANCELED') {
      console.warn('[axios] request canceled', err.config?.url, err.message);
    }
    return Promise.reject(err);
  }
);

if (isNative) {
  // ne jamais envoyer de XSRF en mode Bearer
  delete apiClient.defaults.headers.common['X-XSRF-TOKEN'];
  delete apiClient.defaults.headers.common['X-Requested-With']; // optional
  apiClient.defaults.xsrfCookieName = undefined as any;
  apiClient.defaults.xsrfHeaderName = undefined as any;
} else {
  apiClient.defaults.xsrfCookieName = 'XSRF-TOKEN';
  apiClient.defaults.xsrfHeaderName = 'X-XSRF-TOKEN';
  apiClient.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';
}

apiClient.interceptors.request.use(async config => {
  const t = getTokenSync();
  if (t) { 
    config.headers.Authorization = `Bearer ${t}`; config.withCredentials = false;
    if (config.headers) {
      delete (config.headers as any)['X-XSRF-TOKEN'];
      delete (config.headers as any)['x-xsrf-token'];
      delete (config.headers as any)['X-Requested-With']; // optional
    }
    config.withCredentials = false;
  }
  else { config.withCredentials = true; } 
  
  return config;
});

// @ts-ignore
apiClient.interceptors.request.use((config: AxiosRequestConfig) => {
  if (
    (
      !internalEndpoints.some(endpoint => config.url?.startsWith(endpoint)) || 
      (mobileApiEndpoints.length && mobileApiEndpoints.some(endpoint => config.url?.startsWith(endpoint)))
    ) &&
    !isAbsoluteUrl(config?.url)
  ) {
    config.url = `${import.meta.env.VITE_API_URL}/api/v1/${config.url}`;
  }

  const method = config.method?.toUpperCase();

  // transform array query params in GET request to comma separated string
  if (method === 'GET' && Array.isArray(config.params?.with)) {
    config.params.with = config.params.with.join(',');
  }
  if (method === 'GET' && Array.isArray(config.params?.load)) {
    config.params.load = config.params.load.join(',');
  }
  if (method === 'GET' && Array.isArray(config.params?.loadCount)) {
    config.params.loadCount = config.params.loadCount.join(',');
  }

  // add workspace query parameter
  const workspaceId = getActiveWorkspaceId();
  if (workspaceId) {
    const method = config.method?.toLowerCase();
    if (
      ['get', 'post', 'put'].includes(method!) &&
      config.params?.workspaceId == null
    ) {
      config.params = {...config.params, workspaceId};
    }
  }

  const echoSocketId = getEchoSocketId();
  if (echoSocketId) {
    config.headers = {
      ...config.headers,
      // @ts-ignore
      'X-Socket-ID': echoSocketId,
    };
  }

  // override PUT, DELETE, PATCH methods, they might not be supported on the backend
  if (method === 'PUT' || method === 'DELETE' || method === 'PATCH') {
    config.headers = {
      ...config.headers,
      'X-HTTP-Method-Override': method,
    };
    config.method = 'POST';
    config.params = {
      ...config.params,
      _method: method,
    };
  }

  if (import.meta.env.SSR) {
    config.headers = {
      ...config.headers,
      referer: 'http://localhost',
    };
  }

  return config;
});
